/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
	Box,
	Button,
	TextField,
	IconButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Slider,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import ImageIcon from "@material-ui/icons/Image";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { SERVER_URL } from "../../../../utils";
import { BUTTON_ICON_MAP } from "./buttonIconLibrary";
import { resolveButtonBorderRadiusCss } from "./buttonBorderRadius";
import ButtonHideCountdownFrame from "./ButtonHideCountdownFrame";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import HexColorPickerPopover from "./HexColorPickerPopover";
import InlineQuizEditor from "./InlineQuizEditor";
import { REGISTRATION_PAGE_ID } from "./escapeRoomEditorConstants";
import { getElementsForViewport, getQuizOptionsLayoutForBreakpoint } from "./escapeRoomEditorViewportUtils";
import {
	getQuizBlockBackground,
	getQuizBlockBorderCss,
	getQuizBlockBoxShadow,
	getQuizTrueFalseColorsEditor,
	getQuizOptionStyles,
	QUIZ_OPTION_DESIGNS,
	parseRgba,
	toRgba,
	normalizeColorToRgba,
	parseColorToRgb,
	getDefaultHoverColor,
	resolveQuizOptionBorderPresentation,
	isShapeGradient,
	buildTextAnswerFieldPresentation,
} from "./escapeRoomEditorQuizVisual";
import { getImageElementTransformStyle } from "./escapeRoomImageTransform";
import { genMatchId } from "./escapeRoomElementFactory";
import {
	HANDLE_SIZE,
	RESIZE_EDGE_STRIP_PX,
	resizeHandleStyle,
	hitTestResizeEdge,
	resolveQuizImageUrl,
	REUSABLE_QUIZ_ID,
} from "./escapeRoomEditorCanvasInteraction";
import { MatchQuizPlay, FillBlanksSegmentEdit, FillBlanksQuizPlay, TextAnswerQuizPlay } from "./escapeRoomMatchFillEditorQuiz";
import { QuizPreviewThumbnail, TextFloatingToolbar } from "./escapeRoomEditorFloatingWidgets";
import { getTextEffectPresentation, getElementEffectPresentation, getButtonEffectPresentation, mergeBoxShadow, buttonElementNeedsEffectOverflow, resolveButtonBorderCss, resolveImageBorderCss, pickEffectBundle, hasActiveEffectBundle } from "./textEffectStyles";
import ButtonImageVisual, { isButtonImageMode } from "./ButtonImageVisual";
import { LEGACY_LOGO_REGISTRATION_ROLE } from "./legacyClientRegistration";
import {
	isScoreTextElement,
	ensureScoreTextTemplateHtml,
	resolveScoreTextDisplayHtml,
} from "./escapeRoomScoreText";
import { isEditorUiNode } from "./escapeRoomInlineTextFormat";

function getElementWrapperBorderRadius(element) {
	if (element.type === "shape") return 0;
	if (element.type === "button" || element.type === "hint") {
		const r = element.style?.borderRadius;
		if (typeof r === "number" && Number.isFinite(r) && r >= 0) return r;
		return 8;
	}
	return 4;
}

function quizPersistSideFields(quiz) {
	if (!quiz) return {};
	return {
		pointsCorrect: quiz.pointsCorrect,
		pointsIncorrect: quiz.pointsIncorrect,
		allowMultipleAttempts: quiz.allowMultipleAttempts,
		attemptMode: quiz.attemptMode,
		maxAttempts: quiz.maxAttempts,
		gradingMode: quiz.gradingMode === "survey" ? "survey" : "scored",
		surveySubmitMessage: quiz.surveySubmitMessage != null ? String(quiz.surveySubmitMessage) : "",
	};
}

function ElementRenderer({ element, selected, onSelect, onUpdate, onUpdateQuiz, onDragStart, onResizeStart, preview, isLocked, registrationDesign, canvasZoom = 1, onButtonAction, quizData, simulateQuiz, playQuizAnswer, onQuizOptionSelect, onQuizCustomAnswer, onRequestOptionImagePicker, onQuizHintUsed, onHintPreviewClick, onHintPlayModal, editingTextElementId, setEditingTextElementId, editingButtonLabelElementId, setEditingButtonLabelElementId, allQuizzesOnPageCompleted, simulateButtonHidden, simulateButtonDisabled, playPageAnimKey, playEntryAnimationStyle, onContextMenu: onContextMenuProp, editorBreakpointId = "desktop", playerScore = 0, disableAudioPreview = false }) {
	const editRef = useRef(null);
	const buttonLabelEditRef = useRef(null);
	const textEditingWasActiveRef = useRef(false);
	const lastTextClickRef = useRef({ time: 0, id: null });
	const textDragPendingRef = useRef(null);
	const textMousePosRef = useRef({ x: 0, y: 0 });
	const [hintModal, setHintModal] = useState(null);
	const [isHovered, setIsHovered] = useState(false);
	const [quizHoveredOption, setQuizHoveredOption] = useState(-1);
	const [editingQuizTextAnswerElementId, setEditingQuizTextAnswerElementId] = useState(null);
	const textAnswerInputRef = useRef(null);
	const textAnswerSaveContextRef = useRef(null);
	const textAnswerDraftRef = useRef("");
	const locked = isLocked || (preview && false);
	const handleContextMenu = (e) => {
		if (preview || locked) return;
		e.preventDefault();
		e.stopPropagation();
		if (onContextMenuProp) onContextMenuProp(e, element);
	};

	useLayoutEffect(() => {
		if (selected) return;
		setEditingQuizTextAnswerElementId((prev) => {
			if (prev !== element.elementId) return prev;
			const ctx = textAnswerSaveContextRef.current;
			const newVal = String(
				textAnswerInputRef.current?.value ?? textAnswerDraftRef.current ?? ""
			).trim();
			if (ctx?.onUpdateQuiz && ctx.quizId && newVal !== String(ctx.firstQ?.correctAnswer || "").trim()) {
				ctx.onUpdateQuiz(ctx.quizId, {
					fullName: ctx.fullName,
					questions: [{ ...ctx.firstQ, correctAnswer: newVal, options: [] }],
					...ctx.quizPersistSide,
				});
			}
			return null;
		});
	}, [selected, element.elementId]);

	useEffect(() => {
		if (element.type === "text" && editRef.current && element.content) {
			const isScore = isScoreTextElement(element.content);
			const isEditingText = editingTextElementId === element.elementId;
			if (isEditingText) return;
			const html = isScore
				? resolveScoreTextDisplayHtml(element.content, playerScore, { editing: false })
				: (element.content.html || `<p>${element.content.text || "Texto"}</p>`);
			if (editRef.current.innerHTML !== html) {
				editRef.current.innerHTML = html;
			}
		}
	}, [element.elementId, element.type, element.content?.html, element.content?.text, element.content?.textRole, editingTextElementId, preview, locked, playerScore]);

	const onUpdateRef = useRef(onUpdate);
	onUpdateRef.current = onUpdate;

	useEffect(() => {
		if (element.type !== "text" || preview || locked) return;
		const isEditing = editingTextElementId === element.elementId;
		const wasEditing = textEditingWasActiveRef.current;
		textEditingWasActiveRef.current = isEditing;
		if (!wasEditing || isEditing || !editRef.current) return;
		let html = editRef.current.innerHTML;
		if (isScoreTextElement(element.content)) html = ensureScoreTextTemplateHtml(html);
		const saved = element.content?.html || `<p>${element.content?.text || "Texto"}</p>`;
		if (html !== saved) {
			onUpdateRef.current({ ...element, content: { ...element.content, html } });
		}
	}, [editingTextElementId, element, preview, locked]);

	/** Si cambias de página sin quitar el foco del texto, no hay blur: al desmontar guardamos el HTML en la página que toca */
	useEffect(() => {
		if (element.type !== "text" || preview || locked) return undefined;
		const snap = element;
		return () => {
			const node = editRef.current;
			if (!node) return;
			let html = node.innerHTML;
			if (isScoreTextElement(snap.content)) {
				html = ensureScoreTextTemplateHtml(html);
			}
			const expected = isScoreTextElement(snap.content)
				? resolveScoreTextDisplayHtml(snap.content, playerScore, { editing: true })
				: (snap.content?.html || `<p>${snap.content?.text || "Texto"}</p>`);
			if (html === expected) return;
			queueMicrotask(() => {
				onUpdateRef.current({ ...snap, content: { ...snap.content, html } });
			});
		};
	}, [element.elementId, element.type, preview, locked, playerScore]);

	// No auto-resize de texto: dimensiones fijas controladas por el usuario (estilo Canva).
	// El useEffect anterior medía y actualizaba width/height, causando un ciclo que encogía los textos.

	const hasTransparentBg = element.type === "shape" || element.type === "text" || element.type === "image" || element.type === "hint" || element.type === "button" || element.type === "video" || element.type === "audio";
	const style = {
		position: "absolute",
		left: element.x,
		top: element.y,
		width: element.width,
		height: element.height,
		zIndex: selected && !preview && !locked ? 20 : 1,
		border: preview ? "none" : locked ? "none" : selected ? "2px solid #7c3aed" : "none",
		borderRadius: getElementWrapperBorderRadius(element),
		cursor: preview || locked ? "default" : selected ? "move" : "pointer",
		overflow:
			(element.type === "button" && (
				buttonElementNeedsEffectOverflow(element) ||
				Math.max(0, Number(element.content?.hideAfterSeconds) || 0) > 0
			)) ||
			element.type === "quiz"
				? "visible"
				: "hidden",
		backgroundColor: hasTransparentBg ? "transparent" : "#fff",
		boxSizing: "border-box",
		...(playEntryAnimationStyle || {}),
		// En play, textos/imágenes/formas no deben capturar clics ni tapar botones/quizzes
		...((locked || preview) && (element.type === "text" || element.type === "image" || element.type === "shape")
			? { pointerEvents: "none", cursor: "default" }
			: {}),
	};

	const handleMouseDown = (e) => {
		if (preview || locked) return;
		if (e.button !== 0) return;
		if (e.target.closest(".element-resize-handle")) return;
		if (element.type === "button" && editingButtonLabelElementId === element.elementId && e.target.closest("[data-button-label-edit='1']")) return;
		if (element.type === "quiz" && e.target.closest("[data-er-quiz-no-drag='1']")) return;
		e.stopPropagation();
		e.preventDefault();
		onSelect(element, e);
		onDragStart(element, e);
	};

	const useInsetHandles = true;
	const editingInlineContent =
		(element.type === "text" && editingTextElementId === element.elementId) ||
		(element.type === "button" && editingButtonLabelElementId === element.elementId) ||
		(element.type === "quiz" && editingQuizTextAnswerElementId === element.elementId);
	const ResizeHandles = () =>
		preview || locked || !selected
			? null
			: (
				<>
					{/* Franjas solo en el perímetro: dentro = mover, borde = redimensionar (toda la línea) */}
					{(() => {
						const ez = editingInlineContent ? 44 : 24;
						const stripDown = (e) => {
							if (e.button !== 0) return;
							const root = e.currentTarget.parentElement;
							if (!root) return;
							const edge = hitTestResizeEdge(root, e.clientX, e.clientY);
							e.stopPropagation();
							e.preventDefault();
							if (!edge) return;
							onResizeStart(element, edge, e);
						};
						const t = RESIZE_EDGE_STRIP_PX;
						return (
							<>
								<div
									className="element-resize-edge-strip element-resize-edge-n"
									style={{ position: "absolute", top: 0, left: 0, right: 0, height: t, zIndex: ez, pointerEvents: "auto", cursor: "ns-resize", background: "transparent" }}
									onMouseDown={stripDown}
								/>
								<div
									className="element-resize-edge-strip element-resize-edge-s"
									style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: t, zIndex: ez, pointerEvents: "auto", cursor: "ns-resize", background: "transparent" }}
									onMouseDown={stripDown}
								/>
								<div
									className="element-resize-edge-strip element-resize-edge-w"
									style={{ position: "absolute", top: t, bottom: t, left: 0, width: t, zIndex: ez, pointerEvents: "auto", cursor: "ew-resize", background: "transparent" }}
									onMouseDown={stripDown}
								/>
								<div
									className="element-resize-edge-strip element-resize-edge-e"
									style={{ position: "absolute", top: t, bottom: t, right: 0, width: t, zIndex: ez, pointerEvents: "auto", cursor: "ew-resize", background: "transparent" }}
									onMouseDown={stripDown}
								/>
							</>
						);
					})()}
					{["nw", "n", "ne", "e", "se", "s", "sw", "w"].map((pos) => (
						<div
							key={pos}
							className="element-resize-handle"
							style={{
								...resizeHandleStyle(pos, useInsetHandles),
								pointerEvents: "auto",
								// Por encima del contentEditable al editar texto / etiqueta de botón
								...(editingInlineContent ? { zIndex: 50 } : {}),
							}}
							onMouseDown={(e) => {
								if (e.button !== 0) return;
								e.stopPropagation();
								e.preventDefault();
								onResizeStart(element, pos, e);
							}}
						/>
					))}
				</>
			);

	if (element.type === "text") {
		const s = element.style || {};
		const isScore = isScoreTextElement(element.content);
		const isEditing = editingTextElementId === element.elementId;
		const { className: textEffectClass, extraStyle: textEffectStyle } = getTextEffectPresentation(s, { animated: !preview });
		const textContentStyle = {
			padding: 8,
			fontFamily: s.fontFamily || "Arial, sans-serif",
			fontSize: s.fontSize || 18,
			color: s.color || "#000",
			backgroundColor: s.backgroundColor === "transparent" ? "transparent" : s.backgroundColor || "transparent",
			fontWeight: s.fontWeight || "normal",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			textAlign: s.textAlign || "left",
			letterSpacing: Number.isFinite(Number(s.letterSpacing)) ? `${Number(s.letterSpacing)}px` : "0px",
			lineHeight: Number.isFinite(Number(s.lineHeight)) ? Number(s.lineHeight) : 1.3,
			width: "100%",
			height: "100%",
			minWidth: 20,
			minHeight: 24,
			outline: "none",
			boxSizing: "border-box",
			overflow: "hidden",
			overflowX: "hidden",
			overflowY: "hidden",
			wordBreak: "break-word",
			overflowWrap: "break-word",
			...textEffectStyle,
		};

		const scheduleMeasure = () => {};

		const cancelPendingDrag = () => {
			if (textDragPendingRef.current) {
				clearTimeout(textDragPendingRef.current);
				textDragPendingRef.current = null;
			}
			document.removeEventListener("mouseup", cancelOnMouseUp, true);
			document.removeEventListener("mousemove", updateMousePos, true);
		};
		const updateMousePos = (ev) => {
			textMousePosRef.current = { x: ev.clientX, y: ev.clientY };
		};
		let cancelOnMouseUp;
		const handleTextMouseDown = (e) => {
			if (preview || locked) return;
			if (e.button !== 0) return;
			if (e.target.closest(".element-resize-handle")) return;
			if (e.target.closest(".element-resize-edge-strip")) return;
			e.stopPropagation();
			if (!locked) onSelect(element, e);
			if (isEditing) return;
			const now = Date.now();
			const isDoubleClickIntent = lastTextClickRef.current.id === element.elementId && now - lastTextClickRef.current.time < 400;
			lastTextClickRef.current = { time: now, id: element.elementId };
			if (isDoubleClickIntent) return;
			textMousePosRef.current = { x: e.clientX, y: e.clientY };
			cancelOnMouseUp = () => {
				cancelPendingDrag();
			};
			document.addEventListener("mouseup", cancelOnMouseUp, true);
			document.addEventListener("mousemove", updateMousePos, true);
			textDragPendingRef.current = setTimeout(() => {
				textDragPendingRef.current = null;
				document.removeEventListener("mouseup", cancelOnMouseUp, true);
				document.removeEventListener("mousemove", updateMousePos, true);
				const pos = textMousePosRef.current;
				onDragStart(element, { clientX: pos.x, clientY: pos.y });
			}, 220);
		};
		const handleTextDoubleClick = (e) => {
			e.stopPropagation();
			if (preview || locked) return;
			cancelPendingDrag();
			onSelect(element, e);
			if (setEditingTextElementId) setEditingTextElementId(element.elementId);
			setTimeout(() => {
				if (editRef.current) editRef.current.focus();
			}, 0);
		};
		return (
			<div
				data-er-canvas-el="1"
				style={{
					...style,
					userSelect: isEditing ? "text" : "none",
					cursor: preview || locked ? "default" : isEditing ? "text" : (selected ? "move" : "pointer"),
				}}
				onMouseDown={handleTextMouseDown}
				onDoubleClick={handleTextDoubleClick}
				onClick={(e) => {
					e.stopPropagation();
					if (!locked) onSelect(element, e);
				}}
				onContextMenu={handleContextMenu}
			>
				<ResizeHandles />
				<div
					ref={editRef}
					data-er-text-edit="1"
					className={textEffectClass}
					contentEditable={preview || locked ? false : isEditing}
					suppressContentEditableWarning
					style={textContentStyle}
					onPaste={preview || locked || !isEditing ? undefined : (e) => {
						e.preventDefault();
						const text = e.clipboardData?.getData("text/plain") ?? "";
						if (text !== "") {
							document.execCommand("insertText", false, text);
						}
					}}
					onBlur={preview ? undefined : (e) => {
						let html = e.target.innerHTML;
						if (isScore) html = ensureScoreTextTemplateHtml(html);
						onUpdate({ ...element, content: { ...element.content, html } });
						scheduleMeasure();
						if (isEditorUiNode(e.relatedTarget)) return;
						if (setEditingTextElementId) setEditingTextElementId(null);
					}}
					onInput={preview || locked ? undefined : scheduleMeasure}
				/>
			</div>
		);
	}
	if (element.type === "image") {
		const isLogo = element.registrationRole === "logo" || element.registrationRole === "logoOncommit" || element.registrationRole === LEGACY_LOGO_REGISTRATION_ROLE;
		const imgUrl = isLogo && registrationDesign?.logoUrl
			? registrationDesign.logoUrl
			: element.content?.url;
		const blendMode = element.style?.imageBlendMode || "none";
		const blendColor = normalizeColorToRgba(element.style?.imageBlendColor, 1);
		const blendOpacityRaw = Number(element.style?.imageBlendOpacity);
		const blendOpacity = Number.isFinite(blendOpacityRaw) ? Math.max(0, Math.min(1, blendOpacityRaw)) : 0.35;
		const resolvedImgUrl = imgUrl.startsWith("http") || imgUrl.startsWith("/") || imgUrl.startsWith("data:") ? imgUrl : `${SERVER_URL}/${imgUrl}`;
		const imageMaskId = `img-mask-${String(element.elementId || "image").replace(/[^a-zA-Z0-9_-]/g, "")}`;
		const preserveAspectRatio = isLogo ? "xMidYMid meet" : "xMidYMid slice";
		const imageTransformStyle = getImageElementTransformStyle(element.style);
		const imageMediaWrapStyle = { width: "100%", height: "100%", ...imageTransformStyle };
		return (
			<div
				data-er-canvas-el="1"
				style={style}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element, e); }}
				onContextMenu={handleContextMenu}
			>
				<ResizeHandles />
				{imgUrl ? (
					<div
						style={{
							position: "relative",
							width: "100%",
							height: "100%",
							overflow: "hidden",
							isolation: "isolate",
							boxSizing: "border-box",
							borderRadius: element.style?.borderRadiusPercent != null ? `${element.style.borderRadiusPercent}%` : 0,
							border: resolveImageBorderCss(element.style),
						}}
					>
						{blendMode === "none" ? (
							<div style={imageMediaWrapStyle}>
								<img
									src={resolvedImgUrl}
									alt={element.content?.alt || ""}
									style={{
										width: "100%",
										height: "100%",
										objectFit: isLogo ? "contain" : "cover",
										opacity: element.style?.opacity != null ? element.style.opacity : 1,
									}}
								/>
							</div>
						) : (
							<div style={imageMediaWrapStyle}>
								<svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: "block", opacity: element.style?.opacity != null ? element.style.opacity : 1 }}>
								<defs>
									<mask id={imageMaskId}>
										<image href={resolvedImgUrl} x="0" y="0" width="100" height="100" preserveAspectRatio={preserveAspectRatio} />
									</mask>
								</defs>
								<image href={resolvedImgUrl} x="0" y="0" width="100" height="100" preserveAspectRatio={preserveAspectRatio} />
								<rect
									x="0"
									y="0"
									width="100"
									height="100"
									fill={blendColor}
									fillOpacity={blendOpacity}
									mask={`url(#${imageMaskId})`}
									style={{ mixBlendMode: blendMode === "multiply" ? "multiply" : "normal" }}
								/>
							</svg>
							</div>
						)}
					</div>
				) : (
					<div style={{ padding: 8, color: "#999", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
						{isLogo ? "Logo" : selected ? "URL en panel derecho" : "Imagen"}
					</div>
				)}
			</div>
		);
	}
	if (element.type === "video") {
		const videoStyle = { ...style };
		if (selected && !preview && !locked) videoStyle.overflow = "visible";
		const videoWrapperStyle = {
			position: "absolute",
			inset: 0,
			zIndex: 0,
			overflow: "hidden",
			borderRadius: "inherit",
			pointerEvents: preview ? "none" : (selected && !locked ? "none" : "auto"),
		};
		return (
			<div
				data-er-canvas-el="1"
				style={videoStyle}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element, e); }}
				onContextMenu={handleContextMenu}
			>
				<div style={videoWrapperStyle}>
					{preview ? (
						element.content?.url ? (
							<video
								src={
									element.content.url.startsWith("http") || element.content.url.startsWith("/") || element.content.url.startsWith("data:")
										? element.content.url
										: `${SERVER_URL}/${element.content.url}`
								}
								preload="metadata"
								muted
								playsInline
								loop={!!element.content?.loop}
								style={{ width: "100%", height: "100%", objectFit: "cover", opacity: element.content?.opacity != null ? element.content.opacity / 100 : 1 }}
							/>
						) : (
							<div style={{ padding: 8, color: "#999", fontSize: 12, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#eee" }}>
								<VideoLibraryIcon style={{ fontSize: 24, color: "#999" }} />
							</div>
						)
					) : element.content?.url ? (
						<video
							src={
								element.content.url.startsWith("http") || element.content.url.startsWith("/") || element.content.url.startsWith("data:")
									? element.content.url
									: `${SERVER_URL}/${element.content.url}`
							}
							controls={element.content?.showControls !== false && !selected}
							preload="metadata"
							playsInline
							loop={!!element.content?.loop}
							autoPlay={!!element.content?.autoplay}
							muted={!!element.content?.autoplay}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								opacity: element.content?.opacity != null ? element.content.opacity / 100 : 1,
							}}
						/>
					) : (
						<div style={{ padding: 8, color: "#999", fontSize: 12 }}>
							{selected ? "Multimedia en panel derecho" : "Video"}
						</div>
					)}
				</div>
				<ResizeHandles />
			</div>
		);
	}
	if (element.type === "audio") {
		const resolved = element.content?.url
			? (element.content.url.startsWith("http") || element.content.url.startsWith("/") || element.content.url.startsWith("data:")
				? element.content.url
				: `${SERVER_URL}/${element.content.url}`)
			: "";
		return (
			<div
				data-er-canvas-el="1"
				style={style}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element, e); }}
				onContextMenu={handleContextMenu}
			>
				<div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: preview || locked ? "none" : "1px dashed rgba(148,163,184,0.55)", borderRadius: "inherit", color: "#94a3b8", fontSize: 12, padding: 8, boxSizing: "border-box" }}>
					{resolved ? (preview || (simulateQuiz && locked) ? (
						<audio
							src={resolved}
							preload="metadata"
							loop={!!element.content?.loop}
							autoPlay={!preview && !disableAudioPreview && !!element.content?.autoplay}
							controls={!preview && !element.content?.hiddenInPlay}
							style={{ width: "100%", maxWidth: "100%" }}
						/>
					) : "Audio") : (selected ? "Audio en panel derecho" : "Audio")}
				</div>
				<ResizeHandles />
			</div>
		);
	}
	if (element.type === "quiz") {
		const quiz = quizData;
		const questions = quiz?.questions || [];
		const firstQ = questions[0];
		const opts = firstQ?.options || [];
		const hasQuizContent = quiz && questions.length > 0;
		const answer = simulateQuiz ? (playQuizAnswer || {})[element.elementId] : null;
		const attemptMode = quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single");
		const maxAttempts = Math.max(1, quiz?.maxAttempts ?? 3);
		const isMatch = firstQ?.type === "match";
		const isFillBlanks = firstQ?.type === "fillBlanks";
		const isTextAnswer = firstQ?.type === "textAnswer";
		const isSurveyMode = String(quiz?.gradingMode || "").toLowerCase() === "survey";
		const totalCorrectCount = isMatch
			? (firstQ?.matchPairs || []).length
			: isFillBlanks
				? (firstQ?.segments || []).filter((s) => s?.type === "blank").length
				: isTextAnswer
					? 1
					: opts.filter((o) => o?.correct).length;
		const selections = (answer?.selections || (answer?.selectedIndex != null ? [{ optionIndex: answer.selectedIndex, correct: answer.correct }] : []));
		const correctSelectedCount = selections.filter((s) => s.correct).length;
		const matchDone = isMatch && (answer?.done != null ? answer.done : !!(answer?.pairs && answer.pairs.length >= totalCorrectCount));
		const fillBlanksDone = isFillBlanks && (answer?.done != null ? answer.done : answer?.confirmed);
		const textAnswerDone = isTextAnswer && (answer?.done != null ? answer.done : answer?.confirmed);
		const done = isMatch ? matchDone : isFillBlanks ? fillBlanksDone : isTextAnswer ? textAnswerDone : (attemptMode === "single"
			? selections.length >= 1
			: attemptMode === "limited"
				? correctSelectedCount >= totalCorrectCount || selections.length >= maxAttempts
				: correctSelectedCount >= totalCorrectCount);
		const answered = done;
		const isSimulating = simulateQuiz && hasQuizContent;
		const canEdit = selected && !locked && !simulateQuiz && hasQuizContent && onUpdateQuiz && quiz?._id;

		// FIX: detectar tipo imagen aquí, fuera del IIFE, para usarlo en toda la lógica
		const isImageType = firstQ?.type === "imageChoice" || firstQ?.type === "optionsWithImages";
		const quizOptionsLayout = firstQ ? getQuizOptionsLayoutForBreakpoint(firstQ, editorBreakpointId) : "row";
		const quizOptionLabelWraps = !isMatch && !isFillBlanks && !isTextAnswer && (quizOptionsLayout === "columns" || quizOptionsLayout === "grid" || editorBreakpointId !== "desktop");

		const qStyle = element.style || {};
		const optStyle = element.content?.quizOptionsStyle || {};
		const quizQuestionAlign = element.content?.quizQuestionTextAlign || qStyle.textAlign || "left";
		const textStyle = {
			fontFamily: qStyle.fontFamily || "Arial, sans-serif",
			fontSize: qStyle.fontSize || 14,
			color: qStyle.color || "#1e293b",
			fontWeight: qStyle.fontWeight || "600",
			fontStyle: qStyle.fontStyle || "normal",
			textDecoration: qStyle.textDecoration || "none",
			textAlign: quizQuestionAlign,
		};
		const imageMaxHeight = Math.max(40, Math.min(400, element.content?.quizOptionsImageMaxHeight ?? 120));
		const optionTextStyle = {
			fontFamily: optStyle.fontFamily || "Arial, sans-serif",
			fontSize: optStyle.fontSize || 12,
			color: optStyle.color || "#334155",
			fontWeight: optStyle.fontWeight || "normal",
			fontStyle: optStyle.fontStyle || "normal",
			textDecoration: optStyle.textDecoration || "none",
			textAlign: optStyle.textAlign || "left",
		};
		const defaultOptionTextEffect = getTextEffectPresentation(
			{ ...optStyle, color: optStyle.color || "#334155" },
			{ baseClass: "", animated: isSimulating },
		);
		const quizOptionsCustomBox = element.content?.quizOptionsCustom || {};
		const quizBoxDesignId = element.content?.quizOptionsDesign || "classic";
		const quizBoxDesignDefaults = QUIZ_OPTION_DESIGNS[quizBoxDesignId] || QUIZ_OPTION_DESIGNS.classic;
		const optionBoxFxColor = quizOptionsCustomBox.optionBorderColor || quizBoxDesignDefaults.defaultBorder || optStyle.color || "#334155";
		const optionBoxFx = getElementEffectPresentation(
			{ ...pickEffectBundle(quizOptionsCustomBox, "optionBox"), color: optionBoxFxColor },
			{ animated: isSimulating, preferBoxShadow: true },
		);
		const optionBoxGlitchActive = hasActiveEffectBundle(quizOptionsCustomBox, "optionBox")
			&& pickEffectBundle(quizOptionsCustomBox, "optionBox").textEffect === "glitch";
		const textAnswerFieldPresentation = isTextAnswer
			? buildTextAnswerFieldPresentation({
				element,
				optionTextStyle,
				optionBoxFx,
				state: {
					done: isSimulating ? done : false,
					isSelected: isSimulating && !!answer?.confirmed,
					correct: isSimulating && (isSurveyMode ? true : !!answer?.correct),
					sel: isSimulating && answer?.confirmed ? answer : null,
				},
				context: { index: 0, totalCount: 1, questionType: firstQ?.type, surveyMode: isSurveyMode && isSimulating },
			})
			: null;
		const derivedFillOptions = (segs) => [...new Set((segs || []).filter((s) => s.type === "blank" && (s.correctAnswer || "").trim()).map((s) => (s.correctAnswer || "").trim()))];
		const handleQuestionBlur = (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || "Pregunta";
			if (newText === (firstQ?.text || "")) return;
			const qPayload = isMatch
				? { ...firstQ, text: newText, options: [] }
				: isFillBlanks
					? { ...firstQ, text: newText, fillOptions: derivedFillOptions(firstQ.segments) }
					: isTextAnswer
						? { ...firstQ, text: newText, options: [] }
						: { ...firstQ, text: newText, options: (firstQ.options || []).map((o) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: !!o.correct })) };
			onUpdateQuiz(quiz._id, {
				fullName: newText.slice(0, 80),
				questions: [qPayload],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			});
		};
		const handleCorrectAnswerBlur = (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newVal = String(e?.target?.value ?? textAnswerDraftRef.current ?? "").trim();
			textAnswerDraftRef.current = e?.target?.value ?? textAnswerDraftRef.current;
			const quizSide = {
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			};
			if (newVal !== String(firstQ?.correctAnswer || "").trim()) {
				onUpdateQuiz(quiz._id, {
					fullName: quiz.fullName,
					questions: [{ ...firstQ, correctAnswer: newVal, options: [] }],
					...quizSide,
				});
			}
			if (isEditorUiNode(e.relatedTarget)) return;
			setEditingQuizTextAnswerElementId(null);
		};
		const handleTextAnswerDoubleClick = (e) => {
			e.stopPropagation();
			if (!canEdit || isSimulating) return;
			textAnswerDraftRef.current = String(firstQ?.correctAnswer || "");
			setEditingQuizTextAnswerElementId(element.elementId);
			setTimeout(() => {
				const input = textAnswerInputRef.current;
				if (input) {
					input.focus();
					if (typeof input.select === "function") input.select();
				}
			}, 0);
		};
		const isEditingTextAnswer = !preview && !locked && !isSimulating && editingQuizTextAnswerElementId === element.elementId && !!onUpdateQuiz && !!quiz?._id;
		const quizSidePersist = quizPersistSideFields(quiz);
		if (isTextAnswer && (isEditingTextAnswer || canEdit)) {
			textAnswerSaveContextRef.current = {
				elementId: element.elementId,
				quizId: quiz._id,
				fullName: quiz.fullName,
				firstQ,
				quizPersistSide: quizSidePersist,
				onUpdateQuiz,
			};
		}
		const handleMatchLeftBlur = (leftId) => (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || "";
			const newLeft = (firstQ.matchLeft || []).map((l) => l.id === leftId ? { ...l, text: newText } : l);
			if (newLeft.find((l) => l.id === leftId)?.text === (firstQ.matchLeft || []).find((l) => l.id === leftId)?.text) return;
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, matchLeft: newLeft }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleMatchRightBlur = (rightId) => (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || "";
			const newRight = (firstQ.matchRight || []).map((r) => r.id === rightId ? { ...r, text: newText } : r);
			if (newRight.find((r) => r.id === rightId)?.text === (firstQ.matchRight || []).find((r) => r.id === rightId)?.text) return;
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, matchRight: newRight }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleSegmentBlur = (segIndex, isBlank) => (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const t = e?.target;
			const newVal = (t == null ? "" : (typeof t.value === "string" ? t.value : (t.textContent ?? ""))).trim();
			const segs = [...(firstQ.segments || [])];
			if (!segs[segIndex]) return;
			if (isBlank) {
				if (segs[segIndex].correctAnswer === newVal) return;
				segs[segIndex] = { ...segs[segIndex], correctAnswer: newVal };
			} else {
				if (segs[segIndex].value === newVal) return;
				segs[segIndex] = { ...segs[segIndex], value: newVal };
			}
			const fillOpts = derivedFillOptions(segs);
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, segments: segs, fillOptions: fillOpts }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleAddMatchPair = () => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const leftId = genMatchId();
			const rightId = genMatchId();
			const newLeft = [...(firstQ.matchLeft || []), { id: leftId, text: "Nuevo", imageUrl: "" }];
			const newRight = [...(firstQ.matchRight || []), { id: rightId, text: "Nuevo", imageUrl: "" }];
			const newPairs = [...(firstQ.matchPairs || []), [leftId, rightId]];
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, matchLeft: newLeft, matchRight: newRight, matchPairs: newPairs }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleRemoveMatchPair = (index) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const left = firstQ.matchLeft || [];
			const right = firstQ.matchRight || [];
			const pairCount = Math.min(left.length, right.length);
			if (index < 0 || index >= pairCount || pairCount <= 1) return;
			const leftId = left[index].id;
			const rightId = right[index].id;
			const newLeft = left.filter((_, i) => i !== index);
			const newRight = right.filter((_, i) => i !== index);
			const newPairs = (firstQ.matchPairs || []).filter(([l, r]) => l !== leftId && r !== rightId);
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, matchLeft: newLeft, matchRight: newRight, matchPairs: newPairs }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleAddSegment = (afterIndex, type) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const segs = [...(firstQ.segments || [])];
			const newSeg = type === "blank" ? { type: "blank", blankId: genMatchId(), correctAnswer: "" } : { type: "text", value: "", segmentId: genMatchId() };
			segs.splice(afterIndex + 1, 0, newSeg);
			const fillOpts = derivedFillOptions(segs);
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, segments: segs, fillOptions: fillOpts }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleRemoveSegment = (segIndex) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const segs = (firstQ.segments || []).filter((_, i) => i !== segIndex);
			if (segs.length === 0) return;
			const fillOpts = derivedFillOptions(segs);
			onUpdateQuiz(quiz._id, { fullName: quiz.fullName, questions: [{ ...firstQ, segments: segs, fillOptions: fillOpts }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts, attemptMode: quiz.attemptMode, maxAttempts: quiz.maxAttempts });
		};
		const handleOptionBlur = (i) => (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || `Opción ${i + 1}`;
			const current = opts[i]?.text || "";
			if (newText === current) return;
			const newOpts = opts.map((o, j) => (j === i ? { text: newText, imageUrl: o.imageUrl || "", correct: !!o.correct } : { text: o.text, imageUrl: o.imageUrl || "", correct: !!o.correct }));
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ ...firstQ, options: newOpts }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			});
		};
		const canClickOption = (i) => !done && !selections.some((s) => s.optionIndex === i);
		const handleOptionClick = isSimulating && onQuizOptionSelect
			? (i) => { if (canClickOption(i)) onQuizOptionSelect(element.elementId, i, opts[i]?.correct); }
			: undefined;
		const handleSetCorrectOption = canEdit ? (i) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const isTrueFalse = firstQ.type === "trueFalse";
			const newOpts = isTrueFalse
				? opts.map((o, j) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: j === i }))
				: (() => {
					const toggled = !opts[i]?.correct;
					if (!toggled && opts.filter((o) => o.correct).length <= 1) return opts;
					return opts.map((o, j) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: j === i ? toggled : o.correct }));
				})();
			if (newOpts === opts) return;
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ ...firstQ, options: newOpts }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			});
		} : undefined;
		const handleAddOption = canEdit && firstQ.type !== "trueFalse" ? () => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newOpts = [...opts, { text: "", imageUrl: "", correct: false }];
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ ...firstQ, options: newOpts }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			});
		} : undefined;
		const handleRemoveOption = canEdit && firstQ.type !== "trueFalse" && opts.length > 1 ? (i) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const hadCorrect = opts[i]?.correct;
			let newOpts = opts.filter((_, j) => j !== i);
			if (hadCorrect && newOpts.length > 0 && !newOpts.some((o) => o.correct)) {
				newOpts = newOpts.map((o, j) => (j === 0 ? { ...o, correct: true } : o));
			}
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ ...firstQ, options: newOpts }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
				attemptMode: quiz.attemptMode,
				maxAttempts: quiz.maxAttempts,
			});
		} : undefined;
		const handleOptionImageClick = canEdit && (firstQ.type === "imageChoice" || firstQ.type === "optionsWithImages") && onRequestOptionImagePicker ? (i) => (e) => {
			e.stopPropagation();
			onRequestOptionImagePicker(element.elementId, i);
		} : undefined;
		return (
			<>
			<div
				data-er-canvas-el="1"
				style={{
					...style,
					display: "flex",
					flexDirection: "column",
					overflow: isSimulating || isImageType || isMatch || isTextAnswer ? "hidden" : "visible",
					background: getQuizBlockBackground(element),
					borderRadius: 8,
					boxShadow: getQuizBlockBoxShadow(element),
					border: selected ? "2px solid #7c3aed" : getQuizBlockBorderCss(element),
				}}
				onMouseDown={isSimulating ? (e) => e.stopPropagation() : handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!isSimulating && !locked) onSelect(element, e); }}
				onContextMenu={handleContextMenu}
			>
				<ResizeHandles />
				{/* FIX: padding reducido para tipos imagen, sin gap extra que empuje contenido */}
				<div style={{
					flex: 1,
					overflow: isImageType || isMatch || isTextAnswer ? "hidden" : "visible",
					padding: isImageType ? "6px 6px 6px 6px" : isMatch ? 3 : isTextAnswer ? 6 : 10,
					display: "flex",
					flexDirection: "column",
					gap: isImageType ? 0 : isTextAnswer ? 0 : 8,
					minHeight: 0,
					boxSizing: "border-box",
				}}>
					{hasQuizContent ? (
						<>
							{/* Texto de la pregunta oculto en pantalla (se mantiene en datos para edición en panel) */}
							{false && !isImageType && (firstQ.text || canEdit) && (
								canEdit ? (
									<div
										contentEditable
										suppressContentEditableWarning
										onBlur={handleQuestionBlur}
										onClick={(e) => e.stopPropagation()}
										onMouseDown={(e) => e.stopPropagation()}
										style={{ ...textStyle, marginBottom: 4, flexShrink: 0, outline: "none", cursor: "text", minHeight: 20 }}
									>
										{firstQ.text || "Pregunta"}
									</div>
								) : (
									<div style={{ ...textStyle, marginBottom: 4, flexShrink: 0 }}>
										{firstQ.text}
									</div>
								)
							)}
							{/* Match: interactivo con drag-drop en Reproducir, editable en canvas */}
							{isMatch && (
								(isSimulating && onQuizCustomAnswer) ? (
									<MatchQuizPlay firstQ={firstQ} onConfirm={(data) => onQuizCustomAnswer(element.elementId, "match", data)} done={done} answer={answer} optionTextStyle={optionTextStyle} />
								) : (
									<div data-er-quiz-no-drag="1" style={{ flex: 1, display: "flex", gap: 12, minHeight: 0, overflow: "hidden" }}>
										<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, overflow: "auto" }}>
											{(() => {
												const leftArr = firstQ.matchLeft || [];
												const rightArr = firstQ.matchRight || [];
												const pairCount = Math.min(leftArr.length, rightArr.length);
												return Array.from({ length: pairCount }, (_, i) => {
													const left = leftArr[i];
													const right = rightArr[i];
													return (
														<div key={left.id + "-" + right.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
															<div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0", ...optionTextStyle }}>
																{left.imageUrl && left.imageUrl.trim() ? (
																	<img src={resolveQuizImageUrl(left.imageUrl)} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} />
																) : null}
																{canEdit ? (
																	<span contentEditable suppressContentEditableWarning onPaste={(e) => { e.preventDefault(); const t = e.clipboardData?.getData("text/plain") ?? ""; if (t !== "") document.execCommand("insertText", false, t); }} onBlur={handleMatchLeftBlur(left.id)} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ flex: 1, outline: "none", cursor: "text" }}>{left.text || "—"}</span>
																) : (
																	<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{left.text || "—"}</span>
																)}
															</div>
															{canEdit && (
																<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveMatchPair(i); }} style={{ padding: 4 }} disabled={pairCount <= 1} title="Eliminar pregunta">
																	<DeleteIcon style={{ fontSize: 18 }} />
																</IconButton>
															)}
														</div>
													);
												});
											})()}
											{canEdit && (
												<Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={(e) => { e.stopPropagation(); handleAddMatchPair(); }} style={{ alignSelf: "flex-start", fontSize: 11 }}>Añadir pregunta</Button>
											)}
										</div>
										<div style={{ display: "flex", alignItems: "center", color: "#94a3b8" }}><SwapHorizIcon style={{ fontSize: 20 }} /></div>
										<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, overflow: "auto" }}>
											{(() => {
												const leftArr = firstQ.matchLeft || [];
												const rightArr = firstQ.matchRight || [];
												const pairCount = Math.min(leftArr.length, rightArr.length);
												return Array.from({ length: pairCount }, (_, i) => {
													const right = rightArr[i];
													return (
														<div key={right.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
															<div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0", ...optionTextStyle }}>
																{right.imageUrl && right.imageUrl.trim() ? (
																	<img src={resolveQuizImageUrl(right.imageUrl)} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} />
																) : null}
																{canEdit ? (
																	<span contentEditable suppressContentEditableWarning onPaste={(e) => { e.preventDefault(); const t = e.clipboardData?.getData("text/plain") ?? ""; if (t !== "") document.execCommand("insertText", false, t); }} onBlur={handleMatchRightBlur(right.id)} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ flex: 1, outline: "none", cursor: "text" }}>{right.text || "—"}</span>
																) : (
																	<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{right.text || "—"}</span>
																)}
															</div>
														</div>
													);
												});
											})()}
										</div>
									</div>
								)
							)}
							{/* Fill blanks: interactivo con drag-drop en Reproducir, editable en canvas */}
							{isFillBlanks && (
								(isSimulating && onQuizCustomAnswer) ? (
									<FillBlanksQuizPlay firstQ={firstQ} onConfirm={(data) => onQuizCustomAnswer(element.elementId, "fillBlanks", data)} done={done} answer={answer} optionTextStyle={optionTextStyle} />
								) : (
									<div data-er-quiz-no-drag="1" style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minHeight: 0, overflow: "auto" }}>
										<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, ...optionTextStyle, lineHeight: 1.6 }}>
											{(firstQ.segments || []).map((seg, i) => (
												<div key={seg.blankId || seg.segmentId || `s-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
													<FillBlanksSegmentEdit seg={seg} segIndex={i} isBlank={seg.type === "blank"} canEdit={canEdit} onBlur={handleSegmentBlur} optionTextStyle={optionTextStyle} />
													{canEdit && (
														<span style={{ display: "inline-flex", alignItems: "center", gap: 1, background: "#f1f5f9", borderRadius: 6, padding: "2px 4px", border: "1px solid #cbd5e1" }}>
															<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddSegment(i, seg.type === "text" ? "blank" : "text"); }} style={{ padding: 4, color: "#475569" }} title={seg.type === "text" ? "+ Hueco" : "+ Texto"}>
																<AddIcon style={{ fontSize: 18 }} />
															</IconButton>
															{(firstQ.segments || []).length > 1 && (
																<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveSegment(i); }} style={{ padding: 4, color: "#dc2626" }} title="Eliminar">
																	<DeleteIcon style={{ fontSize: 18 }} />
																</IconButton>
															)}
														</span>
													)}
												</div>
											))}
										</div>
										<div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
											{(firstQ.fillOptions || derivedFillOptions(firstQ.segments)).map((opt, i) => (
												<span key={i} style={{ padding: "6px 12px", background: "#fff", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: (optionTextStyle.fontSize || 12) * 0.95, color: "#1e293b", fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>{opt}</span>
											))}
											{((firstQ.fillOptions || []).length === 0 && derivedFillOptions(firstQ.segments).length === 0) && (
												<span style={{ fontSize: 12, color: "#94a3b8" }}>Escribe las respuestas correctas en cada hueco</span>
											)}
										</div>
									</div>
								)
							)}
							{isTextAnswer && (
								(isSimulating && onQuizCustomAnswer) ? (
									<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}>
										<TextAnswerQuizPlay
											firstQ={firstQ}
											onConfirm={(data) => onQuizCustomAnswer(element.elementId, "textAnswer", data)}
											done={done}
											answer={answer}
											optionTextStyle={optionTextStyle}
											fieldContainerStyle={textAnswerFieldPresentation?.containerStyle}
											fieldInputStyle={textAnswerFieldPresentation?.inputStyle}
											fieldClassName={textAnswerFieldPresentation?.className}
											fieldSubmitStyle={textAnswerFieldPresentation?.submitButtonStyle}
											fieldSubmitClassName={textAnswerFieldPresentation?.submitButtonClassName}
										/>
									</div>
								) : (
									<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}>
										<div
											className={textAnswerFieldPresentation?.className}
											style={{
												...textAnswerFieldPresentation?.containerStyle,
												cursor: canEdit ? (isEditingTextAnswer ? "text" : (selected ? "move" : "pointer")) : "default",
											}}
											onDoubleClick={canEdit ? handleTextAnswerDoubleClick : undefined}
											title={canEdit && !isEditingTextAnswer ? "Doble clic para editar la respuesta correcta" : undefined}
										>
											{isEditingTextAnswer ? (
												<input
													ref={textAnswerInputRef}
													type="text"
													defaultValue={firstQ?.correctAnswer || ""}
													onInput={(e) => {
														textAnswerDraftRef.current = e.target.value;
													}}
													onBlur={handleCorrectAnswerBlur}
													placeholder="Respuesta correcta"
													data-quiz-textanswer-canvas="1"
													onMouseDown={(e) => e.stopPropagation()}
													onClick={(e) => e.stopPropagation()}
													style={textAnswerFieldPresentation?.inputStyle}
												/>
											) : (
												<span style={{
													...textAnswerFieldPresentation?.inputStyle,
													color: (firstQ?.correctAnswer || "").trim()
														? (textAnswerFieldPresentation?.inputStyle?.color || optionTextStyle.color)
														: "#94a3b8",
													display: "flex",
													alignItems: "center",
													alignSelf: "stretch",
													flex: 1,
													userSelect: "none",
												}}
												>
													{(firstQ?.correctAnswer || "").trim() || "Respuesta correcta"}
												</span>
											)}
										</div>
									</div>
								)
							)}
							{/* Tipos estándar: opciones múltiple/imagen */}
							{!isMatch && !isFillBlanks && !isTextAnswer && (() => {
								const layout = quizOptionsLayout;
								const gridCols = Math.min(6, Math.max(1, firstQ.gridColumns || 2));
								let containerStyle = {};
								if (isImageType) {
									if (layout === "columns") {
										// FIX: overflow hidden + height 100% para que no desborde
										containerStyle = {
											flex: 1,
											display: "flex",
											flexDirection: "row",
											flexWrap: "nowrap",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
										};
									} else if (layout === "row") {
										containerStyle = {
											flex: 1,
											display: "flex",
											flexDirection: "column",
											flexWrap: "nowrap",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
										};
									} else {
										// grid
										containerStyle = {
											flex: 1,
											display: "grid",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
											gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
											gridAutoRows: "minmax(0, 1fr)",
										};
									}
								} else {
									const designId = element?.content?.quizOptionsDesign || "classic";
									const design = QUIZ_OPTION_DESIGNS[designId] || QUIZ_OPTION_DESIGNS.classic;
									const isSegmented = design.layout === "segmented";
									const isBigButtons = design.layout === "bigButtons";
									const qcBorder = element?.content?.quizOptionsCustom || {};
									const optBorderW = qcBorder.optionBorderWidth != null && Number.isFinite(Number(qcBorder.optionBorderWidth))
										? Math.max(0, Number(qcBorder.optionBorderWidth))
										: (design.borderWidth ?? 1);
									const borderInset = optBorderW > 0 ? Math.min(4, optBorderW) : 0;
									containerStyle = {
										flex: 1,
										display: layout === "grid" ? "grid" : "flex",
										flexDirection: layout === "columns" ? "row" : "column",
										flexWrap: "nowrap",
										gridTemplateColumns: layout === "grid" ? `repeat(${gridCols}, minmax(0, 1fr))` : undefined,
										gap: isSegmented ? (optBorderW > 0 ? Math.max(4, optBorderW) : 0) : (isBigButtons ? 12 : Math.max(6, optBorderW > 0 ? optBorderW + 2 : 0)),
										minHeight: 0,
										minWidth: 0,
										overflow: "visible",
										padding: borderInset > 0 ? borderInset : undefined,
										boxSizing: "border-box",
									};
								}
								const quizTfColors = getQuizTrueFalseColorsEditor(element);
								// Colores V/F personalizados solo para pregunta tipo trueFalse y solo si están definidos
								const useTfColorsForCard = firstQ?.type === "trueFalse" && (element?.content?.quizTrueColor || element?.content?.quizFalseColor);
								const cardTrueBg = useTfColorsForCard ? (quizTfColors.trueBg || "#f0fdf4") : "#f0fdf4";
								const cardFalseBg = useTfColorsForCard ? (quizTfColors.falseBg || "#fef2f2") : "#fef2f2";
								const cardTrueColor = useTfColorsForCard ? quizTfColors.trueColor : "#22c55e";
								const cardFalseColor = useTfColorsForCard ? quizTfColors.falseColor : "#ef4444";
								return (
							<div style={containerStyle}>
								{opts.map((o, i) => {
									const hasImg = o.imageUrl && o.imageUrl.trim();
									const sel = selections.find((s) => s.optionIndex === i);
									const isSelected = !!sel;
									const bg = done || isSelected
										? (o.correct ? cardTrueBg : isSelected && !sel?.correct ? cardFalseBg : "#fff")
										: (isSimulating ? "#fff" : (o.correct ? cardTrueBg : "#fff"));
									const cardBg = isCardStyle ? "transparent" : bg;
									const borderColor = done || isSelected
										? (o.correct ? cardTrueColor : isSelected && !sel?.correct ? cardFalseColor : "#e2e8f0")
										: (isSimulating ? "#e2e8f0" : (o.correct ? cardTrueColor : "#e2e8f0"));
									const optionLabel = (firstQ.type === "imageChoice" && !o.text) ? `Opción ${i + 1}` : (o.text || "Opción");
									const isCardStyle = isImageType;
									const isHovered = quizHoveredOption === i && handleOptionClick && canClickOption(i);
									const showCorrectAnim = isSelected && o.correct;
									const showIncorrectAnim = isSelected && !sel?.correct;
								const optStyles = !isCardStyle ? getQuizOptionStyles(element, { done, isSelected, correct: o.correct, sel }, { index: i, totalCount: opts.length, questionType: firstQ?.type }) : null;
								const isBigButtons = optStyles?.layout === "bigButtons";
								// Hover en backoffice: color de la opción (Verdadero/Falso) más oscuro o más claro; solo si es trueFalse y tiene colores definidos
								const hoverBaseColor = useTfColorsForCard
									? (o.correct ? cardTrueColor : cardFalseColor)
									: (isCardStyle ? borderColor : (optStyles?.backgroundColor || optStyles?.borderColor));
								const hasCustomHoverBg = !!optStyles?.hoverBackgroundColor;
								const autoHoverBg = getDefaultHoverColor(hoverBaseColor) || hoverBaseColor;
								const resolvedHoverBg = optStyles?.hoverBackgroundColor || autoHoverBg;
								const resolvedHoverText = isHovered && optStyles?.hoverOptionColor
									? optStyles.hoverOptionColor
									: (optStyles?.optionColor || optionTextStyle.color);
								const resolvedOptionTextColor = isHovered ? resolvedHoverText : (optStyles?.optionColor || optionTextStyle.color);
								const { className: optionTextEffectClass, extraStyle: optionTextEffectStyle } = getTextEffectPresentation(
									{ ...optStyle, color: resolvedOptionTextColor },
									{ baseClass: "", animated: isSimulating },
								);
								const optionBorderCol = isHovered && !hasCustomHoverBg ? autoHoverBg : (optStyles?.borderColor || "#e2e8f0");
								const hoverOuterShadow = isHovered ? "0 2px 8px rgba(0,0,0,0.08)" : undefined;
								const optionBorderStyle = optStyles
									? resolveQuizOptionBorderPresentation(
										optStyles.borderWidth,
										optionBorderCol,
										mergeBoxShadow(optStyles.boxShadow, hoverOuterShadow),
									)
									: { border: "none", boxShadow: undefined };
								const cardHoverBorder = getDefaultHoverColor(borderColor) || borderColor;
								const cardContainerBase = {
									display: "flex",
									flexDirection: "column",
									position: "relative",
									borderRadius: 8,
									background: cardBg,
									border: "2px solid",
									borderColor: isHovered ? cardHoverBorder : borderColor,
									overflow: optionBoxGlitchActive ? "visible" : "hidden",
									boxShadow: isHovered ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
									cursor: handleOptionClick ? "pointer" : "default",
									boxSizing: "border-box",
									outline: "none",
									transition: "all 0.2s ease",
									transform: isHovered ? "scale(1.02)" : "scale(1)",
									animation: showCorrectAnim ? "quiz-option-correct 0.5s ease" : showIncorrectAnim ? "quiz-option-incorrect 0.5s ease" : "none",
									flex: "1 1 0",
									minWidth: 0,
									minHeight: 0,
								};
								const listContainerBase = {
									display: "flex",
									alignItems: "center",
									justifyContent: isBigButtons ? "center" : undefined,
									gap: optStyles?.layout === "letter" || optStyles?.layout === "radio" ? 14 : (isBigButtons ? 10 : 6),
									padding: optStyles?.padding,
									borderRadius: optStyles?.borderRadius,
									background: isHovered ? resolvedHoverBg : optStyles?.backgroundColor,
									...optionTextStyle,
									color: isHovered ? resolvedHoverText : (optStyles?.optionColor || optionTextStyle.color),
									cursor: handleOptionClick ? "pointer" : "default",
									minHeight: isBigButtons ? 52 : 28,
									flex: 1,
									minWidth: 0,
									boxSizing: "border-box",
									outline: "none",
									transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
									animation: showCorrectAnim ? "quiz-option-correct 0.5s ease" : showIncorrectAnim ? "quiz-option-incorrect 0.5s ease" : "none",
									fontWeight: isBigButtons ? "600" : optionTextStyle.fontWeight,
									fontSize: isBigButtons ? (optionTextStyle.fontSize || 12) + 2 : optionTextStyle.fontSize,
									...optionBorderStyle,
								};
								const optionContainerBase = isCardStyle ? cardContainerBase : listContainerBase;
								const { boxShadow: optionBoxFxShadow, filter: optionBoxFxFilter, ...optionBoxFxRest } = optionBoxFx.extraStyle || {};
								const optionContainerStyle = {
									...optionContainerBase,
									...optionBoxFxRest,
									...(optionBoxFxFilter ? { filter: optionBoxFxFilter } : {}),
									boxShadow: mergeBoxShadow(optionContainerBase.boxShadow, optionBoxFxShadow),
								};
								return (
									<div
										key={i}
										className={optionBoxFx.className || undefined}
										role={isSimulating ? "button" : undefined}
										tabIndex={isSimulating ? 0 : undefined}
										onClick={handleOptionClick ? (e) => { handleOptionClick(i); e.currentTarget.blur(); } : undefined}
										onKeyDown={handleOptionClick ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); handleOptionClick(i); ev.currentTarget.blur(); } } : undefined}
										onMouseEnter={handleOptionClick ? () => setQuizHoveredOption(i) : undefined}
										onMouseLeave={handleOptionClick ? () => setQuizHoveredOption(-1) : undefined}
										style={optionContainerStyle}
									>
											{isCardStyle ? (
												<>
													<div style={{ position: "relative", flex: 1, minHeight: 0, background: "transparent", overflow: "hidden" }}>
														<div
															style={{
																width: "100%",
																height: "100%",
																minHeight: 0,
																overflow: "hidden",
																background: "transparent",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																cursor: handleOptionImageClick ? "pointer" : undefined,
															}}
															onDoubleClick={handleOptionImageClick ? (e) => { e.stopPropagation(); handleOptionImageClick(i)(e); } : undefined}
															title={handleOptionImageClick ? "Doble clic para elegir imagen" : undefined}
														>
															{hasImg ? (
																<img src={resolveQuizImageUrl(o.imageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} onError={(e) => { e.target.style.display = "none"; }} />
															) : handleOptionImageClick ? (
																<div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", border: "2px dashed #cbd5e1", fontSize: 11, color: "#94a3b8", textAlign: "center", padding: 8, boxSizing: "border-box" }}>
																	Doble clic · imagen
																</div>
															) : (
																<ImageIcon style={{ fontSize: 32, color: "#94a3b8" }} />
															)}
														</div>
														{handleSetCorrectOption && (
															<div style={{ position: "absolute", top: 8, right: 8, zIndex: 2, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
																<div
																	role="button"
																	tabIndex={0}
																	onClick={(e) => { e.stopPropagation(); handleSetCorrectOption(i); }}
																	onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); handleSetCorrectOption(i); } }}
																	style={{
																		width: 48,
																		height: 48,
																		borderRadius: "50%",
																		background: o.correct ? (useTfColorsForCard ? (quizTfColors.trueBg || "#f0fdf4") : "#f0fdf4") : "rgba(255,255,255,0.95)",
																		border: `3px solid ${o.correct ? cardTrueColor : "#cbd5e1"}`,
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		cursor: "pointer",
																		boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
																	}}
																	title={firstQ.type === "trueFalse" ? "Marcar como correcta" : "Correcta / incorrecta"}
																>
																	{o.correct ? <CheckCircleIcon style={{ fontSize: 30, color: "#16a34a" }} /> : <RadioButtonUncheckedIcon style={{ fontSize: 30, color: "#94a3b8" }} />}
																</div>
															</div>
														)}
														{!handleSetCorrectOption && (
															<div style={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}>
																{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: cardTrueColor, background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✓</span>}
																{answered && isSelected && !o.correct && <span style={{ color: cardFalseColor, background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✗</span>}
															</div>
														)}
													</div>
													{/* Solo mostrar texto en optionsWithImages, no en imageChoice */}
													{firstQ.type === "optionsWithImages" && (
														<div style={{
															padding: "4px 6px",
															...optionTextStyle,
															textAlign: "center",
															fontSize: (optionTextStyle.fontSize || 12) * 0.95,
															flexShrink: 0,
															background: "transparent",
														}}>
															{canEdit ? (
																<span
																	contentEditable
																	suppressContentEditableWarning
																	onPaste={(e) => { e.preventDefault(); const t = e.clipboardData?.getData("text/plain") ?? ""; if (t !== "") document.execCommand("insertText", false, t); }}
																	onBlur={handleOptionBlur(i)}
																	onClick={(e) => { e.stopPropagation(); if (handleOptionClick) e.preventDefault(); }}
																	onMouseDown={(e) => e.stopPropagation()}
																	className={defaultOptionTextEffect.className || undefined}
																	style={{ outline: "none", cursor: "text", display: "block", ...defaultOptionTextEffect.extraStyle }}
																>
																	{optionLabel}
																</span>
															) : (
																<span
																	className={defaultOptionTextEffect.className || undefined}
																	style={{
																		...(quizOptionLabelWraps ? { display: "block", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word", lineHeight: 1.35, textAlign: "center" } : { overflow: "hidden", textOverflow: "ellipsis", display: "block", whiteSpace: "nowrap" }),
																		...defaultOptionTextEffect.extraStyle,
																	}}
																>{optionLabel}</span>
															)}
														</div>
													)}
													{handleRemoveOption && (
														<div style={{ position: "absolute", bottom: firstQ.type === "optionsWithImages" ? 28 : 4, right: 4, zIndex: 1 }}>
															<IconButton
																size="small"
																onClick={(e) => { e.stopPropagation(); handleRemoveOption(i); }}
																style={{
																	padding: 6,
																	background: "rgba(254,226,226,0.95)",
																	border: "2px solid #f87171",
																	color: "#b91c1c",
																}}
																title="Eliminar opción"
																disabled={opts.length <= 1}
															>
																<DeleteIcon style={{ fontSize: 20 }} />
															</IconButton>
														</div>
													)}
												</>
											) : (
												<>
													{optStyles?.layout === "letter" && (() => {
														const showTfColors = (answered || !simulateQuiz) || optStyles.applyTfColorsByDefault;
														const letterCircleBg = showTfColors ? (o.correct ? optStyles.letterCircleCorrectBg : optStyles.letterCircleIncorrectBg) : optStyles.letterCircleDefaultBg;
														return (
														<span style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: (answered || !simulateQuiz) ? 18 : 16, fontWeight: 700, background: letterCircleBg, color: showTfColors ? "#fff" : (optStyles.letterCircleDefaultColor || "#475569"), flexShrink: 0 }}>
															{(answered || !simulateQuiz) && (o.correct || (isSelected && !sel?.correct)) ? (o.correct ? "✓" : "✗") : String.fromCharCode(65 + (i % 26))}
														</span>
														);
													})()}
													{optStyles?.layout === "radio" && (() => {
														const showTfColors = (answered || !simulateQuiz) || optStyles.applyTfColorsByDefault;
														return (
														<span style={{ width: optStyles.radioSize || 26, height: optStyles.radioSize || 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", background: showTfColors ? (o.correct ? (optStyles.trueColor || "#22c55e") : (optStyles.falseColor || "#ef4444")) : "transparent", border: `2px solid ${showTfColors ? (o.correct ? (optStyles.trueColor || "#22c55e") : (optStyles.falseColor || "#ef4444")) : "#94a3b8"}`, flexShrink: 0 }}>
															{(answered || !simulateQuiz) && (o.correct ? "✓" : (isSelected && !sel?.correct ? "✗" : "✓"))}
														</span>
														);
													})()}
													{handleSetCorrectOption ? (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSetCorrectOption(i); }} style={{ padding: 4, color: o.correct ? "#22c55e" : "#94a3b8" }} title={firstQ.type === "trueFalse" ? "Marcar como correcta" : "Marcar/desmarcar correcta"}>
															{o.correct ? <CheckCircleIcon style={{ fontSize: 20 }} /> : <RadioButtonUncheckedIcon style={{ fontSize: 20 }} />}
														</IconButton>
													) : (optStyles && optStyles.layout !== "letter" && optStyles.layout !== "radio" && optStyles.iconLeft) ? (
														<span style={{ width: 20, flexShrink: 0, textAlign: "center", fontSize: 14 }}>
															{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: optStyles.optionColor || optStyles.trueColor || "#22c55e" }}>✓</span>}
															{answered && isSelected && !o.correct && <span style={{ color: optStyles.optionColor || optStyles.falseColor || "#ef4444" }}>✗</span>}
														</span>
													) : null}
													{canEdit ? (
														<span
															contentEditable
															suppressContentEditableWarning
															onPaste={(e) => { e.preventDefault(); const t = e.clipboardData?.getData("text/plain") ?? ""; if (t !== "") document.execCommand("insertText", false, t); }}
															onBlur={handleOptionBlur(i)}
															onClick={(e) => { e.stopPropagation(); if (handleOptionClick) e.preventDefault(); }}
															onMouseDown={(e) => e.stopPropagation()}
															className={optionTextEffectClass || undefined}
															style={{
																flex: isBigButtons ? "0 1 auto" : 1,
																outline: "none",
																padding: 2,
																borderRadius: 4,
																minWidth: 20,
																cursor: "text",
																textAlign: optionTextStyle.textAlign,
																lineHeight: 1.35,
																color: resolvedOptionTextColor,
																...(quizOptionLabelWraps || isBigButtons
																	? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word" }
																	: { overflow: "hidden", whiteSpace: "nowrap" }),
																...optionTextEffectStyle,
															}}
														>
															{optionLabel}
														</span>
													) : (
														<span
															className={optionTextEffectClass || undefined}
															style={{
																flex: isBigButtons ? "0 1 auto" : 1,
																minWidth: 0,
																textAlign: optionTextStyle.textAlign,
																lineHeight: 1.35,
																color: resolvedOptionTextColor,
																...(quizOptionLabelWraps || isBigButtons
																	? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word" }
																	: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
																...optionTextEffectStyle,
															}}
														>
															{optionLabel}
														</span>
													)}
													{handleRemoveOption ? (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveOption(i); }} style={{ padding: 4 }} title="Eliminar opción" disabled={opts.length <= 1}>
															<DeleteIcon style={{ fontSize: 18 }} />
														</IconButton>
													) : (optStyles && optStyles.iconRight) ? (
														<span style={{ width: 16, flexShrink: 0, textAlign: "right", fontSize: 14 }}>
															{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: optStyles.optionColor || optStyles.trueColor || "#22c55e" }}>✓</span>}
															{answered && isSelected && !o.correct && <span style={{ color: optStyles.optionColor || optStyles.falseColor || "#ef4444" }}>✗</span>}
														</span>
													) : null}
												</>
											)}
										</div>
									);
								})}
							</div>
							);})()}
							{/* FIX: botón añadir opción solo para tipos estándar, no match/fillBlanks/imagen */}
							{handleAddOption && !isImageType && !isMatch && !isFillBlanks && !isTextAnswer && (
								<button
									type="button"
									onClick={(e) => { e.stopPropagation(); handleAddOption(); }}
									style={{
										display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
										border: "1px dashed #cbd5e1", borderRadius: 8, background: "#f8fafc",
										cursor: "pointer", fontSize: 13, color: "#64748b", alignSelf: "flex-start",
										flexShrink: 0,
									}}
								>
									<AddIcon style={{ fontSize: 18 }} /> Añadir opción
								</button>
							)}
							{/* FIX: para imagen tipo imagen, botón añadir fuera del flujo normal como overlay */}
							{handleAddOption && isImageType && !isMatch && !isFillBlanks && !isTextAnswer && (
								<div style={{ flexShrink: 0, paddingTop: 4 }}>
									<button
										type="button"
										onClick={(e) => { e.stopPropagation(); handleAddOption(); }}
										style={{
											display: "flex", alignItems: "center", gap: 4, padding: "4px 8px",
											border: "1px dashed #cbd5e1", borderRadius: 6, background: "rgba(248,250,252,0.95)",
											cursor: "pointer", fontSize: 11, color: "#64748b", width: "100%",
											justifyContent: "center",
										}}
									>
										<AddIcon style={{ fontSize: 14 }} /> Añadir opción
									</button>
								</div>
							)}
							{answered && firstQ.explanation && (
								<div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 4, padding: 6, background: "#f8fafc", borderRadius: 6, flexShrink: 0 }}>
									💡 {firstQ.explanation}
								</div>
							)}
						</>
					) : (
						<div style={{ padding: 8, color: "#94a3b8", fontSize: 12, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
							{selected ? "Asigna un quiz en el panel" : "Quiz sin asignar"}
						</div>
					)}
				</div>
			</div>
		</>
		);
	}
	if (element.type === "shape") {
		const shapeStyle = element.style || {};
		const shapeFx = getElementEffectPresentation(shapeStyle, { animated: !preview });
		const c = element.content || {};
		const fill = c.fillColor || "rgba(100, 100, 255, 0.8)";
		const stroke = c.strokeColor || "rgba(0, 0, 0, 1)";
		const strokeW = c.strokeWidth ?? 2;
		const shapeType = c.shapeType || "square";
		const radius = c.borderRadius ?? 0;
		const fillIsGrad = isShapeGradient(fill);
		const strokeIsGrad = isShapeGradient(stroke);

		const fillBg = fillIsGrad ? { background: fill } : { backgroundColor: fill };
		const strokeBorder = strokeIsGrad ? {} : { border: `${strokeW}px solid ${stroke}` };

		let innerStyle = {
			width: "100%",
			height: "100%",
			...fillBg,
			...strokeBorder,
			boxSizing: "border-box",
		};

		if (shapeType === "circle") {
			innerStyle = { ...innerStyle, borderRadius: "50%" };
		} else if (shapeType === "triangle") {
			innerStyle = null;
		} else if (shapeType === "line") {
			innerStyle = {
				width: "100%",
				height: Math.max(strokeW, 2),
				minHeight: Math.max(strokeW, 2),
				...(strokeIsGrad ? { background: stroke } : { backgroundColor: stroke }),
				border: "none",
				alignSelf: "center",
				borderRadius: radius ? `${Math.min(radius, 999)}px` : 0,
			};
		} else {
			innerStyle.borderRadius = radius ? `${Math.min(radius, 999)}px` : 0;
		}

		const needsStrokeWrapper = strokeIsGrad && strokeW > 0 && shapeType !== "line";
		const innerRadius = shapeType === "circle" ? "50%" : (radius ? `${Math.min(radius, 999)}px` : 0);
		const wrapperStyle = needsStrokeWrapper ? {
			width: "100%", height: "100%", padding: strokeW, background: stroke, borderRadius: shapeType === "circle" ? "50%" : (radius + strokeW), boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center",
		} : null;
		const wrappedInnerStyle = needsStrokeWrapper ? {
			flex: 1, minWidth: 0, minHeight: 0, ...fillBg, borderRadius: innerRadius, boxSizing: "border-box",
		} : innerStyle;

		return (
			<div
				data-er-canvas-el="1"
				className={shapeFx.className || undefined}
				style={{
					...style,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					...shapeFx.extraStyle,
				}}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element, e); }}
				onContextMenu={handleContextMenu}
			>
				<ResizeHandles />
				{shapeType === "triangle" ? (
					<svg
						width="100%"
						height="100%"
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						style={{ overflow: "visible" }}
					>
						{fillIsGrad && (
							<defs>
								<linearGradient id={`fill-grad-${element.elementId}`} x1="0%" y1="0%" x2="100%" y2="100%">
									{(() => {
										const m = fill.match(/linear-gradient\s*\(\s*\d+\s*deg\s*,\s*(.+?)\s*\d+%\s*,\s*(.+?)\s*\d+%\s*\)/);
										if (m) return [<stop key="1" offset="0%" stopColor={m[1].trim()} />, <stop key="2" offset="100%" stopColor={m[2].trim()} />];
										return null;
									})()}
								</linearGradient>
							</defs>
						)}
						{strokeIsGrad && (
							<defs>
								<linearGradient id={`stroke-grad-${element.elementId}`} x1="0%" y1="0%" x2="100%" y2="100%">
									{(() => {
										const m = stroke.match(/linear-gradient\s*\(\s*\d+\s*deg\s*,\s*(.+?)\s*\d+%\s*,\s*(.+?)\s*\d+%\s*\)/);
										if (m) return [<stop key="1" offset="0%" stopColor={m[1].trim()} />, <stop key="2" offset="100%" stopColor={m[2].trim()} />];
										return null;
									})()}
								</linearGradient>
							</defs>
						)}
						<polygon
							points="50,5 95,95 5,95"
							fill={fillIsGrad ? `url(#fill-grad-${element.elementId})` : fill}
							stroke={strokeIsGrad ? `url(#stroke-grad-${element.elementId})` : stroke}
							strokeWidth={strokeW}
							strokeLinejoin={radius > 0 ? "round" : "miter"}
							vectorEffect="non-scaling-stroke"
						/>
					</svg>
				) : needsStrokeWrapper ? (
					<div style={wrapperStyle}>
						<div style={wrappedInnerStyle} />
					</div>
				) : (
					<div style={innerStyle} />
				)}
			</div>
		);
	}
	if (element.type === "hint") {
		// En modo juego: ocultar el botón de pista una vez respondido el test de la página
		if (simulateQuiz && allQuizzesOnPageCompleted === true) return null;
		const c = element.content || {};
		const label = c.label || "Pista";
		const hintText = c.hintText || "";
		const hintPenalty = c.hintPenalty ?? 0;
		const hasHintDesign = !!c.hintModalDesign;
		const hasConfirmDesign = !!c.confirmModalDesign;
		const s = element.style || {};
		const borderW = Math.max(0, s.borderWidth ?? 1);
		const borderColor = s.borderColor || "rgba(124, 58, 237, 1)";
		const baseBg = s.backgroundColor || "#f5f3ff";
		const hoverBg = s.hoverBackgroundColor ?? getDefaultHoverColor(baseBg);
		const showHover = isHovered && (!locked || simulateQuiz);
		const btnStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: showHover && hoverBg ? hoverBg : baseBg,
			color: s.color || "#7c3aed",
			fontSize: s.fontSize || 14,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: resolveButtonBorderRadiusCss(s),
			border: borderW > 0 ? `${borderW}px solid ${borderColor}` : "none",
			cursor: "pointer",
			transition: "background-color 0.15s ease",
		};
		const handleHintClick = (e) => {
			e.stopPropagation();
			if (preview) {
				// En thumbnails: usar callback del padre para que el modal se renderice fuera
				if (hintText.trim() && onHintPreviewClick) {
					onHintPreviewClick(hintText);
				} else if (hintText.trim()) {
					setHintModal({ type: "hint", text: hintText });
				}
				return;
			}
			if (locked && !simulateQuiz) return; // En play mode (simulateQuiz) permitir el modal
			if (!simulateQuiz) { onSelect(element, e); return; }
			const canOpen = hintText.trim() || hasHintDesign || hasConfirmDesign;
			if (!canOpen) return;
			const shouldConfirm = hintPenalty > 0 && (hasConfirmDesign || hintText.trim());
			const payload = shouldConfirm
				? { type: "confirm", penalty: hintPenalty, text: hintText, label, elementId: element.elementId }
				: { type: "hint", text: hintText, elementId: element.elementId };
			if (onHintPlayModal) {
				onHintPlayModal(payload);
			} else {
				if (shouldConfirm) setHintModal({ type: "confirm", penalty: hintPenalty, text: hintText, label });
				else setHintModal({ type: "hint", text: hintText });
			}
		};
		const handleConfirmAccept = () => {
			setHintModal({ type: "hint", text: hintModal.text });
			if (onQuizHintUsed && hintPenalty > 0) onQuizHintUsed(element.elementId, hintPenalty);
		};
		return (
			<>
				<div
					data-er-canvas-el="1"
					{...(preview && hintText.trim() && { "data-hint-preview": "1", "data-hint-text": hintText })}
					style={{ ...style, ...((preview || simulateQuiz) && { cursor: "pointer" }), ...(preview && hintText.trim() && { pointerEvents: "auto" }) }}
					onMouseDown={handleMouseDown}
					onClick={handleHintClick}
					onContextMenu={handleContextMenu}
				>
					<ResizeHandles />
					<div
						style={{ ...btnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
						onMouseEnter={() => (!locked || simulateQuiz) && setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
					>
						<LiveHelpIcon style={{ fontSize: (s.fontSize || 14) * 1.2 }} />
						{label}
					</div>
				</div>
				{!onHintPlayModal && hintModal?.type === "confirm" && hintModal.penalty === hintPenalty && (
					<Dialog
						open
						onClose={() => setHintModal(null)}
						ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001, animation: "er-modal-overlay-in 0.22s ease-out both" } } }}
						PaperProps={{ style: { zIndex: 10001, animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" } }}
					>
						<DialogTitle>Pista</DialogTitle>
						<DialogContent>Vas a perder {hintModal.penalty} puntos. ¿Estás seguro?</DialogContent>
						<DialogActions>
							<Button onClick={() => setHintModal(null)}>Cancelar</Button>
							<Button onClick={handleConfirmAccept} variant="contained" color="primary">Aceptar</Button>
						</DialogActions>
					</Dialog>
				)}
				{!onHintPlayModal && hintModal?.type === "hint" && (
					<Dialog
						open
						onClose={() => setHintModal(null)}
						ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001, animation: "er-modal-overlay-in 0.22s ease-out both" } } }}
						PaperProps={{ style: { zIndex: 10001, animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" } }}
					>
						<DialogTitle>Pista</DialogTitle>
						<DialogContent><div style={{ whiteSpace: "pre-wrap" }}>{hintModal.text}</div></DialogContent>
						<DialogActions>
							<Button onClick={() => setHintModal(null)} variant="contained" color="primary">Cerrar</Button>
						</DialogActions>
					</Dialog>
				)}
			</>
		);
	}
	if (element.type === "button") {
		const showWhen = element.content?.showWhen || "always";
		if (simulateQuiz && showWhen === "quizCompleted" && allQuizzesOnPageCompleted === false) {
			return null;
		}
		if (simulateQuiz && simulateButtonHidden) return null;
		const s = element.style || {};
		const label = element.content?.label || "";
		const icon = element.content?.icon || "";
		const bg = s.backgroundColor || "transparent";
		const hasBg = bg && bg !== "transparent";
		const radiusCss = resolveButtonBorderRadiusCss(s);
		const hoverBg = s.hoverBackgroundColor ?? (hasBg ? getDefaultHoverColor(bg) : "rgba(0,0,0,0.06)");
		const hoverTextColor = (s.hoverColor || "").trim();
		const disabledPreview = simulateQuiz && simulateButtonDisabled;
		const showHover = !disabledPreview && isHovered && (!locked || simulateQuiz);
		const showLabel = String(label).trim();
		const showIcon = String(icon || "").trim();
		const displayText = !showLabel && !showIcon ? "Clic aquí" : null;
		const isEditingButtonLabel = editingButtonLabelElementId === element.elementId && selected && !preview && !locked && !simulateQuiz && !!setEditingButtonLabelElementId;
		const ta = s.textAlign || "center";
		const flexJustify = ta === "left" ? "flex-start" : ta === "right" ? "flex-end" : "center";
		const btnFx = getButtonEffectPresentation(s, element.content || {}, { animated: !preview, disabled: disabledPreview });
		const btnStyleBase = disabledPreview
			? {
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: flexJustify,
				gap: 6,
				backgroundColor: "#e5e7eb",
				color: "#6b7280",
				fontSize: s.fontSize || 16,
				fontWeight: s.fontWeight || "bold",
				fontStyle: s.fontStyle || "normal",
				textDecoration: s.textDecoration || "none",
				fontFamily: s.fontFamily || "Arial, sans-serif",
				borderRadius: radiusCss,
				border: resolveButtonBorderCss(s, { disabled: true }),
				boxShadow: "none",
				cursor: "not-allowed",
			}
			: {
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: flexJustify,
				gap: 6,
				backgroundColor: showHover && hoverBg ? hoverBg : bg,
				color: showHover && hoverTextColor ? hoverTextColor : (s.color || "rgba(51, 51, 51, 1)"),
				fontSize: s.fontSize || 16,
				fontWeight: s.fontWeight || "bold",
				fontStyle: s.fontStyle || "normal",
				textDecoration: s.textDecoration || "none",
				fontFamily: s.fontFamily || "Arial, sans-serif",
				borderRadius: radiusCss,
				border: resolveButtonBorderCss(s, { showHover, hasBg }),
				boxShadow: hasBg ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
				cursor: preview ? "default" : isEditingButtonLabel ? "text" : "pointer",
				transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
			};
		const { boxShadow: fxBoxShadow, filter: fxFilter, ...btnFxRest } = btnFx.buttonExtraStyle || {};
		const btnStyle = {
			...btnStyleBase,
			...btnFxRest,
			...(fxFilter ? { filter: fxFilter } : {}),
			boxShadow: mergeBoxShadow(btnStyleBase.boxShadow, fxBoxShadow),
		};
		const labelEffectStyle = btnFx.labelExtraStyle || {};
		const labelEffectClass = btnFx.labelClassName || "";
		const iconPos = element.content?.iconPosition || "before";
		const iconScale = element.content?.iconSize ?? 1.2;
		const iconFontSize = (s.fontSize || 16) * iconScale;
		const IconComponent = showIcon && BUTTON_ICON_MAP[icon];
		const labelShown = showLabel || displayText;
		const iconEffectStyle = btnFx.iconExtraStyle || {};
		const iconEffectClass = btnFx.iconClassName || "";
		const iconSpan = showIcon
			? (IconComponent
				? (
					<IconComponent
						className={iconEffectClass || undefined}
						style={{ fontSize: iconFontSize, color: "currentColor", ...iconEffectStyle }}
					/>
				)
				: (
					<span className={iconEffectClass || undefined} style={{ fontSize: iconFontSize, ...iconEffectStyle }}>
						{icon}
					</span>
				))
			: null;
		const textSpan = labelShown
			? isEditingButtonLabel
				? (
					<span
						ref={buttonLabelEditRef}
						data-button-label-edit="1"
						contentEditable
						suppressContentEditableWarning
						onPaste={(e) => {
							e.preventDefault();
							const text = e.clipboardData?.getData("text/plain") ?? "";
							if (text !== "") document.execCommand("insertText", false, text);
						}}
						onBlur={() => {
							const t = (buttonLabelEditRef.current?.textContent ?? "").trim();
							onUpdate({ ...element, content: { ...element.content, label: t } });
							setEditingButtonLabelElementId(null);
						}}
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => e.stopPropagation()}
						className={labelEffectClass || undefined}
						style={{
							outline: "none",
							minWidth: 4,
							cursor: "text",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
							maxWidth: "100%",
							...labelEffectStyle,
						}}
					>
						{showLabel || displayText}
					</span>
				)
				: (
					<span
						onDoubleClick={(e) => {
							e.stopPropagation();
							if (preview || locked || simulateQuiz || !setEditingButtonLabelElementId) return;
							onSelect(element, e);
							setEditingButtonLabelElementId(element.elementId);
							setTimeout(() => {
								const eln = buttonLabelEditRef.current;
								if (eln) {
									eln.focus();
									try {
										const r = document.createRange();
										r.selectNodeContents(eln);
										r.collapse(false);
										const sel = window.getSelection();
										sel.removeAllRanges();
										sel.addRange(r);
									} catch (_) {}
								}
							}, 30);
						}}
						className={labelEffectClass || undefined}
						style={{ userSelect: selected ? "none" : undefined, ...labelEffectStyle }}
					>
						{showLabel || displayText}
					</span>
				)
			: null;
		const handleClick = (e) => {
			e.stopPropagation();
			if (simulateQuiz && simulateButtonDisabled) return;
			if (onButtonAction && element.content?.action) {
				onButtonAction(element);
				return;
			}
			if (locked) return;
			onSelect(element, e);
		};
		const pulseHeartbeat = !!element.content?.continuousPulse && !disabledPreview && !preview;
		const imageMode = isButtonImageMode(element.content);
		const btnInnerStyle = imageMode
			? {
				width: "100%",
				height: "100%",
				display: "flex",
				padding: 0,
				backgroundColor: "transparent",
				border: disabledPreview ? resolveButtonBorderCss(s, { disabled: true }) : "none",
				boxShadow: "none",
				cursor: preview ? "default" : "pointer",
				borderRadius: radiusCss,
				overflow: "hidden",
				...(disabledPreview ? { opacity: 0.55 } : {}),
			}
			: btnStyle;
		const { boxShadow: imgFxBoxShadow, filter: imgFxFilter, ...imgFxRest } = imageMode ? (btnFx.buttonExtraStyle || {}) : {};
		const btnInnerFinal = imageMode
			? {
				...btnInnerStyle,
				...imgFxRest,
				...(imgFxFilter ? { filter: imgFxFilter } : {}),
				boxShadow: mergeBoxShadow(btnInnerStyle.boxShadow, imgFxBoxShadow),
			}
			: btnStyle;
		const hideAfterSec = Math.max(0, Number(element.content?.hideAfterSeconds) || 0);
		const showHideCountdown = !!simulateQuiz && hideAfterSec > 0;
		const buttonBody = (
			<div
				className={[pulseHeartbeat ? "er-editor-btn-heartbeat" : null, btnFx.buttonClassName].filter(Boolean).join(" ") || undefined}
				style={btnInnerFinal}
				onMouseEnter={() => { if (!disabledPreview && (!locked || simulateQuiz)) setIsHovered(true); }}
				onMouseLeave={() => setIsHovered(false)}
			>
				{imageMode ? (
					<ButtonImageVisual
						content={element.content}
						style={s}
						isHovered={showHover}
						disabled={disabledPreview}
						placeholder={selected ? "Elegir imagen en panel" : "Imagen"}
					/>
				) : (
					<>
						{iconPos === "before" ? iconSpan : textSpan}
						{iconPos === "before" ? textSpan : iconSpan}
					</>
				)}
			</div>
		);
		return (
			<div
				data-er-canvas-el="1"
				style={{
					...style,
					overflow: showHideCountdown || style?.overflow === "visible" ? "visible" : style?.overflow,
				}}
				onMouseDown={handleMouseDown}
				onClick={handleClick}
				onContextMenu={handleContextMenu}
			>
				<ResizeHandles />
				{showHideCountdown ? (
					<ButtonHideCountdownFrame durationSec={hideAfterSec} borderRadiusCss={radiusCss}>
						{buttonBody}
					</ButtonHideCountdownFrame>
				) : (
					buttonBody
				)}
			</div>
		);
	}
	return null;
}

export { ElementRenderer };
